/**
 * @fileoverview Mobile Fan View component for the "Cronómetro" game dynamic (Tile ID 50) of Club Atlético de San Luis (ADSL).
 * Fully implements [HU-06]: "Experiencia Móvil del Aficionado y Gamificación de Alta Precisión (/cronometer Fan View)".
 * High-precision timer powered by performance.now() and requestAnimationFrame, "Modo Pleno Sol" high-contrast UI,
 * lead registration form with LFPDPPP consent checkboxes and anti-SQLi sanitization,
 * instant single-tap touch response, intermittent visual challenge mode, live WebSocket synchronization,
 * and celebratory confetti for qualifiers.
 * 
 * @module components/Dashboard/Sections/CronometerFan
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Confetti from 'react-confetti';
import * as BL from '../../../utilities/BL/Cronometer';
import * as DL from '../../../utilities/DL/Cronometer';
import {
    ConnectWebSocketForCronometerFan,
    DisconnectWebSocketForCronometerFan,
    SendPlayerStopped,
    SendRegisterPlayer
} from '../../../utilities/WS/CronometerFanWS';

const DEFAULT_PRIVACY = `AVISO DE PRIVACIDAD SIMPLIFICADO - CLUB ATLÉTICO DE SAN LUIS
En estricto cumplimiento con la Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP), sus datos personales (Nombre completo, Correo electrónico y Teléfono de contacto) serán tratados por el Club Atlético de San Luis (ADSL) y la plataforma tecnológica Interaktia para las siguientes finalidades:
1. Validar su identidad y participación en la dinámica interactiva "Cronómetro".
2. Gestionar la clasificación y entrega de premios oficiales del club a los ganadores de cada fase.
3. Brindarle atención, aclaraciones y seguimiento respecto a las dinámicas del estadio.
4. Finalidad Secundaria (Opcional): Enviarle promociones exclusivas, noticias, preventas y beneficios de patrocinadores de ADSL, únicamente en caso de haber marcado la casilla voluntaria de publicidad.

Sus datos personales están resguardados con estrictas medidas de seguridad técnicas, físicas y administrativas. En cualquier momento puede revocar su consentimiento o ejercer sus derechos ARCO contactando a los canales oficiales del club.`;

const DEFAULT_TERMS = `TÉRMINOS Y CONDICIONES - DINÁMICA CRONÓMETRO ADSL
1. Organizador: La dinámica "Cronómetro" es organizada por el Club Atlético de San Luis (ADSL).
2. Participación: Abierta a todos los aficionados presentes en el estadio o siguiendo la transmisión oficial. Cada participante podrá registrarse una sola vez por jornada con su número telefónico y correo electrónico.
3. Mecánica de Juego: En cada fase se proyectará un número objetivo (Z). El participante debe detener su cronómetro táctil en el segundo objetivo dentro del margen de tolerancia establecido por la directiva.
4. Filtro Piramidal: La clasificación a fases sucesivas (Fase 2, Fase 3, etc.) está reservada exclusivamente para los participantes que hayan clasificado en la fase previa inmediata.
5. Descalificación: El uso de scripts automatizados, intentos de inyección de código, desincronización deliberada de red o alteración de datos resultará en descalificación inmediata e inapelable por parte del comité organizador.
6. Resultados y Premios: Las decisiones del sistema y de los jueces del Club Atlético de San Luis son definitivas e inapelables.`;

const TopGameLogo = () => (
    <div className="CronometerFan__TopLogo">
        <img src="/img/page/adsl-five.png" alt="ADSL Five" />
    </div>
);

const InteraktiaFooter = ({ extraClass = '' }) => (
    <footer className={`CronometerFan__Footer ${extraClass}`.trim()}>
        Hecho con ♥ por{' '}
        <a
            href="https://interaktia.com/?utm_source=cronometer_web&utm_medium=footer&utm_campaign=fdm_adsl"
            target="_blank"
            rel="noopener noreferrer"
        >
            Interaktia
        </a>
    </footer>
);

const InteraktiaFooterV2 = ({ extraClass = '' }) => (
    <footer className={`CronometerFan__Footer ${extraClass}`.trim()}>
        Hecho con ♥ por Interaktia
    </footer>
);

export const CronometerFan = () => {
    const [searchParams] = useSearchParams();
    const trafficSource = searchParams.get('s') || searchParams.get('utm_medium') || searchParams.get('src') || 'obs';

    // Máquina de estados: 'loading' | 'form' | 'instructions' | 'waiting' | 'countdown' | 'playing' | 'stopped' | 'classified' | 'eliminated'
    const [phase, setPhase] = useState('loading');
    const phaseRef = useRef(phase);
    useEffect(() => {
        phaseRef.current = phase;
    }, [phase]);

    // Estado del Formulario de Registro
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        abonado: '',
        acceptedPrivacy: false,
        acceptedTerms: false,
        acceptedMarketing: false
    });
    const [formErrors, setFormErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formStartedTracked, setFormStartedTracked] = useState(false);

    // Estado del Jugador y Sesión
    const [player, setPlayer] = useState(null);
    const [currentPhase, setCurrentPhase] = useState(1);
    const [wonPhases, setWonPhases] = useState({});
    const [serverStatus, setServerStatus] = useState('idle');

    // Parámetros de la fase actual
    const [targetZ, setTargetZ] = useState(9);
    const targetZRef = useRef(targetZ);
    useEffect(() => {
        targetZRef.current = targetZ;
    }, [targetZ]);

    const [isIntermitente, setIsIntermitente] = useState(false);

    // Cuenta regresiva
    const [countdown, setCountdown] = useState(3);

    // Temporizador de alta precisión (performance.now + requestAnimationFrame)
    const [displayTime, setDisplayTime] = useState('0.00');
    const startTimeRef = useRef(0);
    const animIdRef = useRef(null);
    const countdownIntervalRef = useRef(null);
    const autoClassifiedTimerRef = useRef(null);

    // Modales de eliminación / no clasificación
    const [eliminatedMessage, setEliminatedMessage] = useState('No clasificaste a la siguiente fase.');
    const [eliminatedTime, setEliminatedTime] = useState('');

    // Modales Legales
    const [showPrivacyModal, setShowPrivacyModal] = useState(false);
    const [showTermsModal, setShowTermsModal] = useState(false);

    // Dimensiones de ventana para Confetti
    const [windowDimensions, setWindowDimensions] = useState({
        width: typeof window !== 'undefined' ? window.innerWidth : 400,
        height: typeof window !== 'undefined' ? window.innerHeight : 800
    });

    useEffect(() => {
        const handleResize = () => {
            setWindowDimensions({
                width: window.innerWidth,
                height: window.innerHeight
            });
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Bloqueo de gestos de navegación y scroll nativo en fases de juego
    useEffect(() => {
        const preventTouchMove = (e) => {
            if (['playing', 'countdown', 'stopped', 'classified'].includes(phaseRef.current)) {
                if (e.cancelable) e.preventDefault();
            }
        };
        document.addEventListener('touchmove', preventTouchMove, { passive: false });
        return () => document.removeEventListener('touchmove', preventTouchMove);
    }, []);

    // Helper para mostrar pantalla de eliminación
    const triggerEliminatedScreen = useCallback((title, timeVal = '') => {
        if (animIdRef.current) {
            cancelAnimationFrame(animIdRef.current);
            animIdRef.current = null;
        }
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        }
        phaseRef.current = 'eliminated';
        setPhase('eliminated');
        setEliminatedMessage(title || 'No clasificaste a la siguiente fase.');
        setEliminatedTime(timeVal ? String(timeVal) : '');
        localStorage.setItem('cronometer_failed', 'true');
        if (timeVal) {
            localStorage.setItem('cronometer_last_time', String(timeVal));
        }
    }, []);

    // Función del loop del cronómetro con requestAnimationFrame y performance.now()
    const runTimerLoop = useCallback(() => {
        if (phaseRef.current !== 'playing') return;

        const now = performance.now();
        const elapsed = (now - startTimeRef.current) / 1000;
        const currentZ = targetZRef.current || 9;
        const maxAllowedTime = currentZ + 6;

        // Tolerancia máxima: 6 segundos posteriores al objetivo definido
        if (elapsed >= maxAllowedTime) {
            if (animIdRef.current) {
                cancelAnimationFrame(animIdRef.current);
                animIdRef.current = null;
            }
            phaseRef.current = 'stopped';
            setPhase('stopped');
            const formatted = elapsed.toFixed(2);
            setDisplayTime(formatted);

            SendPlayerStopped(elapsed);
            localStorage.setItem('cronometer_failed', 'true');
            localStorage.setItem('cronometer_reason', 'timeout');
            triggerEliminatedScreen('No detuviste el cronómetro.');
            return;
        }

        setDisplayTime(elapsed.toFixed(2));
        animIdRef.current = requestAnimationFrame(runTimerLoop);
    }, [triggerEliminatedScreen]);

    // Función para arrancar la fase con cuenta regresiva (3, 2, 1) y luego pasar a PLAYING
    const startPhaseCountdown = useCallback((cfg) => {
        const faseNum = Number(cfg.fase || 1);
        const zNum = Number(cfg.z || 9);
        const isInterm = Boolean(cfg.intermitente);

        // Filtro piramidal: si fase > 1 y no clasificó en la fase previa, queda eliminado
        const currentSession = BL.GetSession() || {};
        const playerWonPhases = currentSession.wonPhases || {};

        if (faseNum > 1 && !playerWonPhases[faseNum - 1]) {
            triggerEliminatedScreen('No clasificaste a la siguiente fase.');
            return;
        }

        // Si ya había fallado en esta sesión, ignorar el arranque
        if (localStorage.getItem('cronometer_failed') === 'true') {
            return;
        }

        if (animIdRef.current) {
            cancelAnimationFrame(animIdRef.current);
            animIdRef.current = null;
        }
        if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
        }
        if (autoClassifiedTimerRef.current) {
            clearTimeout(autoClassifiedTimerRef.current);
            autoClassifiedTimerRef.current = null;
        }

        setCurrentPhase(faseNum);
        setTargetZ(zNum);
        setIsIntermitente(isInterm);
        setDisplayTime('0.00');

        let count = 3;
        setCountdown(count);
        phaseRef.current = 'countdown';
        setPhase('countdown');

        countdownIntervalRef.current = setInterval(() => {
            count--;
            if (count > 0) {
                setCountdown(count);
            } else {
                clearInterval(countdownIntervalRef.current);
                countdownIntervalRef.current = null;

                phaseRef.current = 'playing';
                setPhase('playing');
                startTimeRef.current = performance.now();
                animIdRef.current = requestAnimationFrame(runTimerLoop);
            }
        }, 1000);
    }, [runTimerLoop, triggerEliminatedScreen]);

    // Manejador central de mensajes del WebSocket
    const handleWebSocketMessage = useCallback((envelope) => {
        if (!envelope) return;
        const eventType = (envelope.type || envelope.Type || '').toLowerCase();
        const payload = envelope.payload !== undefined
            ? envelope.payload
            : (envelope.data !== undefined ? envelope.data : envelope);

        switch (eventType) {
            case 'crono_game_state':
                if (payload.status) setServerStatus(payload.status);
                if (payload.currentPhase) setCurrentPhase(Number(payload.currentPhase));
                if (payload.phase) setCurrentPhase(Number(payload.phase));
                if (payload.targetZ) setTargetZ(Number(payload.targetZ));
                if (payload.intermitente !== undefined) setIsIntermitente(Boolean(payload.intermitente));
                if (payload.participant) {
                    setPlayer(payload.participant);
                    if (payload.participant.wonPhases) {
                        setWonPhases(payload.participant.wonPhases);
                    }
                }
                break;

            case 'crono_change_phase': {
                const newPhase = Number(payload.currentPhase || payload.phase || payload.fase || 1);
                setCurrentPhase(newPhase);
                setServerStatus('idle');
                break;
            }

            case 'crono_start_phase':
                setServerStatus('playing');
                startPhaseCountdown(payload);
                break;

            case 'crono_stop_result': {
                if (animIdRef.current) {
                    cancelAnimationFrame(animIdRef.current);
                    animIdRef.current = null;
                }
                const formattedTime = (typeof payload.time === 'number' ? payload.time : Number(payload.time) || 0).toFixed(2);
                setDisplayTime(formattedTime);

                const isWon = payload.isClassified === true || payload.status === 'won';
                const phaseNum = Number(payload.phase || currentPhase);

                if (isWon) {
                    localStorage.removeItem('cronometer_failed');
                    localStorage.removeItem('cronometer_last_time');
                    localStorage.removeItem('cronometer_reason');
                    BL.UpdateSessionPhases(phaseNum, true, payload.time);
                    setWonPhases(prev => ({ ...prev, [phaseNum]: true }));

                    phaseRef.current = 'classified';
                    setPhase('classified');
                } else {
                    BL.UpdateSessionPhases(phaseNum, false, payload.time);
                    localStorage.setItem('cronometer_failed', 'true');
                    localStorage.setItem('cronometer_last_time', formattedTime);

                    const targetVal = targetZRef.current || 9;
                    const isTimeout = payload.time >= (targetVal + 6);

                    if (isTimeout) {
                        localStorage.setItem('cronometer_reason', 'timeout');
                        triggerEliminatedScreen('No detuviste el cronómetro.');
                    } else {
                        triggerEliminatedScreen('Detuviste el cronómetro en:', formattedTime);
                    }
                }
                break;
            }

            case 'crono_player_eliminated': {
                if (animIdRef.current) {
                    cancelAnimationFrame(animIdRef.current);
                    animIdRef.current = null;
                }
                localStorage.setItem('cronometer_failed', 'true');
                if (payload.reason === 'timeout') {
                    localStorage.setItem('cronometer_reason', 'timeout');
                    triggerEliminatedScreen('No detuviste el cronómetro.');
                } else {
                    triggerEliminatedScreen(payload.message || 'No clasificaste a la siguiente fase.');
                }
                break;
            }

            case 'crono_end_phase': {
                setServerStatus('ended');
                if (phaseRef.current === 'playing') {
                    if (animIdRef.current) {
                        cancelAnimationFrame(animIdRef.current);
                        animIdRef.current = null;
                    }
                    localStorage.setItem('cronometer_failed', 'true');
                    localStorage.setItem('cronometer_reason', 'timeout');
                    triggerEliminatedScreen('No detuviste el cronómetro.');
                }
                break;
            }

            case 'crono_reset_game': {
                setServerStatus('idle');
                localStorage.removeItem('cronometer_failed');
                localStorage.removeItem('cronometer_last_time');
                localStorage.removeItem('cronometer_reason');
                if (animIdRef.current) {
                    cancelAnimationFrame(animIdRef.current);
                    animIdRef.current = null;
                }
                if (countdownIntervalRef.current) {
                    clearInterval(countdownIntervalRef.current);
                    countdownIntervalRef.current = null;
                }
                if (autoClassifiedTimerRef.current) {
                    clearTimeout(autoClassifiedTimerRef.current);
                    autoClassifiedTimerRef.current = null;
                }

                setCurrentPhase(1);
                setWonPhases({});
                setDisplayTime('0.00');

                if (payload && payload.purged) {
                    // Purga total de la dinámica (Zona Peligrosa / Nuevo Partido):
                    // Se borran los datos del aficionado en localStorage para iniciar limpio
                    BL.ClearSession();
                    phaseRef.current = 'form';
                    setPhase('form');
                } else {
                    // Reinicio de partida / ronda:
                    // Se mantiene el registro del usuario (nombre, WhatsApp, correo) para que no tenga
                    // que volver a llenar el formulario, pero se resetean las fases ganadas en el storage.
                    const saved = BL.GetSession();
                    if (saved && saved.name) {
                        BL.SaveSession({
                            ...saved,
                            wonPhases: {},
                            phaseReached: 1,
                            isWinner: false
                        });
                        phaseRef.current = 'waiting';
                        setPhase('waiting');
                    } else {
                        phaseRef.current = 'form';
                        setPhase('form');
                    }
                }
                break;
            }

            default:
                break;
        }
    }, [currentPhase, startPhaseCountdown, triggerEliminatedScreen]);

    // Conectar WebSocket y registrar manejadores
    const initWebSocket = useCallback(async () => {
        await ConnectWebSocketForCronometerFan(handleWebSocketMessage, () => {});
    }, [handleWebSocketMessage]);

    // Rehidratación de Sesión al Montar el Componente
    useEffect(() => {
        BL.TrackPageView('/cronometer');
        BL.TrackQrScan(trafficSource);

        const checkSavedSession = async () => {
            const hasFailed = localStorage.getItem('cronometer_failed') === 'true';
            const lastTime = localStorage.getItem('cronometer_last_time');
            const reason = localStorage.getItem('cronometer_reason');

            if (hasFailed) {
                await initWebSocket();
                if (reason === 'timeout') {
                    triggerEliminatedScreen('No detuviste el cronómetro.');
                } else if (lastTime) {
                    triggerEliminatedScreen('Detuviste el cronómetro en:', lastTime);
                } else {
                    triggerEliminatedScreen('No clasificaste a la siguiente fase.');
                }
                return;
            }

            const session = BL.GetSession();
            if (session && session.name) {
                setPlayer(session);
                if (session.wonPhases) {
                    setWonPhases(session.wonPhases);
                }
                await initWebSocket();
                phaseRef.current = 'waiting';
                setPhase('waiting');
            } else {
                phaseRef.current = 'form';
                setPhase('form');
            }
        };

        checkSavedSession();

        return () => {
            if (animIdRef.current) cancelAnimationFrame(animIdRef.current);
            if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
            if (autoClassifiedTimerRef.current) clearTimeout(autoClassifiedTimerRef.current);
            DisconnectWebSocketForCronometerFan();
        };
    }, [initWebSocket, trafficSource, triggerEliminatedScreen]);

    // Manejo de foco en inputs para telemetría
    const handleInputFocus = () => {
        if (!formStartedTracked) {
            setFormStartedTracked(true);
            BL.TrackFormStart();
        }
    };

    // Manejo de inputs del formulario
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    // Envío del formulario de registro
    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setFormErrors({});

        // Validaciones locales con anti-SQLi
        const errors = {};

        const nameSanitized = await BL.SanitizeTextInput(formData.name, 'Nombre', 100, true);
        if (!nameSanitized.isValid) {
            errors.name = nameSanitized.message;
        }

        const emailValidation = await BL.NormalizeAndValidateEmail(formData.email);
        if (!emailValidation.isValid) {
            errors.email = emailValidation.message;
        }

        const phoneValidation = await BL.NormalizeAndValidatePhone(formData.phone);
        if (!phoneValidation.isValid) {
            errors.phone = phoneValidation.message;
        }

        if (!formData.acceptedPrivacy) {
            errors.acceptedPrivacy = 'Debes aceptar el Aviso de Privacidad para continuar.';
        }

        if (!formData.acceptedTerms) {
            errors.acceptedTerms = 'Debes aceptar los Términos y Condiciones para participar.';
        }

        if (!formData.abonado || (formData.abonado !== 'Sí' && formData.abonado !== 'No')) {
            errors.abonado = 'Por favor selecciona si eres abonad@ o no.';
        }

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        setIsSubmitting(true);

        try {
            const isAbonado = formData.abonado === 'Sí';
            const registrationPayload = {
                name: nameSanitized.sanitized,
                email: emailValidation.email,
                phone: phoneValidation.phone,
                abonado: formData.abonado,
                acceptedTerms: true,
                acceptedMarketing: Boolean(formData.acceptedMarketing),
                trafficSource
            };

            const response = await DL.RegisterParticipant(registrationPayload);

            if (!response.success && !response.participantId) {
                setFormErrors({ general: response.message || 'Error al procesar el registro. Intenta de nuevo.' });
                setIsSubmitting(false);
                return;
            }

            const participantId = response.participantId || Date.now();
            const sessionData = {
                participantId,
                name: registrationPayload.name,
                email: registrationPayload.email,
                phone: registrationPayload.phone,
                isAbonado,
                wonPhases: {},
                phaseReached: response.phaseReached || 1,
                isWinner: Boolean(response.isWinner)
            };

            BL.SaveSession(sessionData);
            setPlayer(sessionData);

            // Telemetría dual
            BL.TrackFormComplete(participantId, isAbonado);

            // Conectar WebSocket y registrar socket
            await initWebSocket();
            SendRegisterPlayer({
                participantId,
                name: sessionData.name,
                email: sessionData.email,
                phone: sessionData.phone,
                abonado: formData.abonado,
                acceptedTerms: true,
                acceptedMarketing: formData.acceptedMarketing,
                trafficSource
            });

            setIsSubmitting(false);
            phaseRef.current = 'instructions';
            setPhase('instructions');
        } catch (error) {
            setIsSubmitting(false);
            setFormErrors({ general: 'Ocurrió un error al procesar tu solicitud. Por favor intenta nuevamente.' });
        }
    };

    // Aceptar instrucciones
    const handleAcceptInstructions = () => {
        phaseRef.current = 'waiting';
        setPhase('waiting');
    };

    // Evento táctil de parada de cronómetro (Single-Tap instantáneo con pointerdown)
    const handleTouchStop = (e) => {
        if (e && e.cancelable) {
            e.preventDefault();
        }

        if (phaseRef.current === 'playing') {
            if (animIdRef.current) {
                cancelAnimationFrame(animIdRef.current);
                animIdRef.current = null;
            }

            phaseRef.current = 'stopped';
            setPhase('stopped');

            const now = performance.now();
            const elapsed = (now - startTimeRef.current) / 1000;
            const roundedTime = Math.round(elapsed * 100) / 100;
            const formatted = roundedTime.toFixed(2);

            setDisplayTime(formatted);
            SendPlayerStopped(roundedTime);
            BL.TrackDynamicEvent('game_stopped', { time: roundedTime });
        }
    };

    return (
        <div 
            className={`CronometerFan ${phase === 'form' ? 'CronometerFan--scrollable' : 'CronometerFan--locked'}`}
            style={{
                backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.78), rgba(15, 23, 42, 0.88)), url('/img/games/cronometer/background-1.png')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
            }}
        >
            {/* Celebración con Confeti para clasificados */}
            {phase === 'classified' && (
                <Confetti
                    width={windowDimensions.width}
                    height={windowDimensions.height}
                    numberOfPieces={160}
                    recycle={false}
                    colors={['#0066ff', '#16a34a', '#dc2626', '#f59e0b', '#ffffff']}
                    style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 100001 }}
                />
            )}

            {/* ================================================================= */}
            {/* 1. FORMULARIO DE REGISTRO (MODO PLENO SOL)                        */}
            {/* ================================================================= */}
            {phase === 'form' && (
                <div 
                    id="form-container" 
                    className="CronometerFan__FormContainer"
                    style={{
                        backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.75), rgba(15, 23, 42, 0.85)), url('/img/games/cronometer/background-1.png')`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat'
                    }}
                >
                    <div className="CronometerFan__RegisterContainer animate__animated animate__fadeIn flex-grow-1 w-100">
                        <div className="text-center mb-3">
                            <img src='/img/page/adsl-five.png' style={{ width: '300px', display: 'block', margin: '0 auto' }} />
                            <h1 className="font-cinzel text-3xl font-black text-white mb-1">
                                Cronómetro
                            </h1>
                            <p className="text-white-50 small mb-0">
                                Detén el cronómetro en el segundo exacto y clasifica a la siguiente fase
                            </p>
                        </div>

                        <div className="CronometerFan__Card">
                            {formErrors.general && (
                                <div className="alert alert-danger py-2 px-3 small rounded-3 mb-3 text-start">
                                    {formErrors.general}
                                </div>
                            )}

                            <form id="regForm" onSubmit={handleFormSubmit} noValidate>
                                {/* Nombre completo */}
                                <div className="mb-3 text-start">
                                    <label htmlFor="name" className="form-label small fw-bold text-white mb-1">
                                        Nombre completo
                                    </label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        onFocus={handleInputFocus}
                                        required
                                        className={`form-control CronometerFan__Input ${formErrors.name ? 'is-invalid border-danger' : ''}`}
                                        placeholder="Tu nombre completo"
                                        autoComplete="name"
                                    />
                                    {formErrors.name && (
                                        <div className="invalid-feedback-custom text-danger small mt-1">{formErrors.name}</div>
                                    )}
                                </div>

                                {/* Correo electrónico */}
                                <div className="mb-3 text-start">
                                    <label htmlFor="email" className="form-label small fw-bold text-white mb-1">
                                        Correo electrónico
                                    </label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        onFocus={handleInputFocus}
                                        required
                                        className={`form-control CronometerFan__Input ${formErrors.email ? 'is-invalid border-danger' : ''}`}
                                        placeholder="tu.correo@ejemplo.com"
                                        autoComplete="email"
                                    />
                                    {formErrors.email && (
                                        <div className="invalid-feedback-custom text-danger small mt-1">{formErrors.email}</div>
                                    )}
                                </div>

                                {/* WhatsApp / Teléfono */}
                                <div className="mb-3 text-start">
                                    <label htmlFor="whatsapp" className="form-label small fw-bold text-white mb-1">
                                        Teléfono / WhatsApp (10 dígitos)
                                    </label>
                                    <input
                                        type="tel"
                                        id="whatsapp"
                                        name="phone"
                                        maxLength="10"
                                        value={formData.phone}
                                        onChange={handleInputChange}
                                        onFocus={handleInputFocus}
                                        required
                                        className={`form-control CronometerFan__Input ${formErrors.phone ? 'is-invalid border-danger' : ''}`}
                                        placeholder="10 dígitos (ej. 4441234567)"
                                        autoComplete="tel"
                                    />
                                    {formErrors.phone && (
                                        <div className="invalid-feedback-custom text-danger small mt-1">{formErrors.phone}</div>
                                    )}
                                </div>

                                {/* ¿Eres abonado? */}
                                <div className="mb-3 text-start">
                                    <label htmlFor="abonado" className="form-label small fw-bold text-white mb-1">
                                        ¿Eres abonad@?
                                    </label>
                                    <select
                                        id="abonado"
                                        name="abonado"
                                        value={formData.abonado}
                                        onChange={handleInputChange}
                                        required
                                        className={`form-select CronometerFan__Input ${formErrors.abonado ? 'is-invalid border-danger' : ''}`}
                                    >
                                        <option value="" disabled>Selecciona una opción</option>
                                        <option value="Sí">Sí</option>
                                        <option value="No">No</option>
                                    </select>
                                    {formErrors.abonado && (
                                        <div className="invalid-feedback-custom text-danger small mt-1">{formErrors.abonado}</div>
                                    )}
                                </div>

                                {/* Casilla 1: Aviso de Privacidad (Obligatoria) */}
                                <div className="form-check mb-2 text-start">
                                    <input
                                        type="checkbox"
                                        id="privacy"
                                        name="acceptedPrivacy"
                                        checked={formData.acceptedPrivacy}
                                        onChange={handleInputChange}
                                        required
                                        className={`form-check-input ${formErrors.acceptedPrivacy ? 'is-invalid' : ''}`}
                                    />
                                    <label htmlFor="privacy" className="form-check-label small text-white" style={{ fontSize: '0.78rem', lineHeight: '1.4' }}>
                                        He leído y acepto el{' '}
                                        <button
                                            type="button"
                                            className="btn btn-link p-0 text-warning small border-0 align-baseline text-decoration-underline"
                                            style={{ fontSize: '0.78rem' }}
                                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowPrivacyModal(true); }}
                                        >
                                            Aviso de Privacidad
                                        </button>.
                                    </label>
                                    {formErrors.acceptedPrivacy && (
                                        <div className="text-danger small mt-1">{formErrors.acceptedPrivacy}</div>
                                    )}
                                </div>

                                {/* Casilla 2: Términos y Condiciones (Obligatoria) */}
                                <div className="form-check mb-3 text-start">
                                    <input
                                        type="checkbox"
                                        id="terms"
                                        name="acceptedTerms"
                                        checked={formData.acceptedTerms}
                                        onChange={handleInputChange}
                                        required
                                        className={`form-check-input ${formErrors.acceptedTerms ? 'is-invalid' : ''}`}
                                    />
                                    <label htmlFor="terms" className="form-check-label small text-white" style={{ fontSize: '0.78rem', lineHeight: '1.4' }}>
                                        He leído y acepto los{' '}
                                        <button
                                            type="button"
                                            className="btn btn-link p-0 text-warning small border-0 align-baseline text-decoration-underline"
                                            style={{ fontSize: '0.78rem' }}
                                            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowTermsModal(true); }}
                                        >
                                            Términos y Condiciones
                                        </button>.
                                    </label>
                                    {formErrors.acceptedTerms && (
                                        <div className="text-danger small mt-1">{formErrors.acceptedTerms}</div>
                                    )}
                                </div>

                                {/* Casilla 3: Publicidad Comercial (Opcional, desmarcada por defecto) */}
                                <div className="form-check mb-4 text-start">
                                    <input
                                        type="checkbox"
                                        id="marketing"
                                        name="acceptedMarketing"
                                        checked={formData.acceptedMarketing}
                                        onChange={handleInputChange}
                                        className="form-check-input"
                                    />
                                    <label htmlFor="marketing" className="form-check-label small text-white-50" style={{ fontSize: '0.75rem', lineHeight: '1.4' }}>
                                        Deseo recibir promociones y novedades comerciales exclusivas del Club Atlético de San Luis.
                                    </label>
                                </div>

                                <button type="submit" className="btn CronometerFan__BtnGold w-100" disabled={isSubmitting}>
                                    {isSubmitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                            ENTRANDO...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-play-fill me-1 fs-5"></i> ¡PARTICIPAR AHORA!
                                        </>
                                    )}
                                </button>
                            </form>
                        </div>
                        <InteraktiaFooter extraClass="CronometerFan__Footer--form" />
                    </div>
                </div>
            )}

            {/* ================================================================= */}
            {/* 2. MODAL DE INSTRUCCIONES                                          */}
            {/* ================================================================= */}
            {phase === 'instructions' && (
                <div id="instructionsModal" className="overlay" style={{ display: 'flex' }}>
                    <TopGameLogo />
                    <div className="instructions-box">
                        <h2>INSTRUCCIONES</h2>
                        <p className="instructions-text">
                            En la pantalla del estadio aparecerá un número objetivo. Tu reto es detener el cronómetro en ese número para avanzar a la siguiente fase.
                        </p>
                        <button
                            type="button"
                            className="btn-submit"
                            onClick={handleAcceptInstructions}
                        >
                            ¡ENTENDIDO!
                        </button>
                    </div>
                    <InteraktiaFooter />
                </div>
            )}

            {/* ================================================================= */}
            {/* 3. SALA DE ESPERA INTERACTIVA                                     */}
            {/* ================================================================= */}
            {phase === 'waiting' && (
                <div className="overlay" style={{ display: 'flex' }}>
                    <TopGameLogo />
                    <div className="waiting-box">
                        <div className="waiting-box__phase-badge">
                            FASE {currentPhase}
                        </div>
                        <div className="waiting-box__status">
                            <span className="waiting-box__status--dot"></span>
                            <span>EN LÍNEA</span>
                        </div>
                        <h2 className="waiting-box__title">
                            {player?.name ? `¡Hola, ${player.name.split(' ')[0]}!` : '¡LISTO!'}
                        </h2>
                        {currentPhase > 1 && !wonPhases[currentPhase - 1] ? (
                            <div className="waiting-box__spectator-notice">
                                ℹ️ La dinámica ya se encuentra en la <strong>Fase {currentPhase}</strong>.<br />
                                Al ser una fase de eliminación para clasificados de la Fase {currentPhase - 1}, puedes seguir la emoción en la pantalla del estadio.
                            </div>
                        ) : serverStatus === 'playing' && phase === 'waiting' && currentPhase === 1 ? (
                            <div className="waiting-box__spectator-notice" style={{ background: '#e0f2fe', color: '#0369a1', borderColor: '#38bdf8' }}>
                                ⏱️ La Fase 1 está corriendo en este momento en el estadio.<br />
                                ¡Tu registro quedó guardado con éxito para la siguiente oportunidad!
                            </div>
                        ) : (
                            <p className="waiting-box__subtitle">
                                Esperando inicio de fase...<br />
                                ¡Mantente atento a la pantalla del estadio!
                            </p>
                        )}
                        {(wonPhases[currentPhase] || wonPhases[currentPhase - 1]) && (
                            <div className="waiting-box__qualified-badge">
                                ✓ Clasificaste para la siguiente fase
                            </div>
                        )}
                    </div>
                    <InteraktiaFooter />
                </div>
            )}

            {/* ================================================================= */}
            {/* 4. OVERLAY DE CUENTA REGRESIVA (3, 2, 1)                          */}
            {/* ================================================================= */}
            {phase === 'countdown' && (
                <div className="overlay overlay--countdown" style={{ display: 'flex' }}>
                    {countdown}
                </div>
            )}

            {/* ================================================================= */}
            {/* 5. ÁREA DE JUEGO (GAMEPLAY Y SINGLE-TAP)                           */}
            {/* ================================================================= */}
            {(phase === 'playing' || phase === 'stopped' || phase === 'classified' || phase === 'countdown') && (
                <div id="gameArea" style={{ display: 'flex' }}>
                    {/* Superficie táctil single-tap */}
                    <div
                        className="touch-area"
                        id="touchArea"
                        onPointerDown={handleTouchStop}
                    ></div>

                    {/* Logotipo oficial superior */}
                    <TopGameLogo />

                    {/* Temporizador gigante en tabular-nums */}
                    <div
                        id="timer"
                        className={isIntermitente && phase === 'playing' ? 'timer-intermitente' : ''}
                    >
                        {displayTime}
                    </div>

                    {/* Cápsula interactiva para pulsar */}
                    {phase === 'playing' && (
                        <div className="tap-prompt">
                            TOCA LA PANTALLA PARA DETENER
                        </div>
                    )}

                    {/* Mensaje de clasificación exitosa */}
                    {phase === 'classified' && (
                        <div id="message" style={{ display: 'block' }}>
                            CLASIFICAS A LA SIGUIENTE FASE
                        </div>
                    )}

                    <InteraktiaFooterV2 />
                </div>
            )}

            {/* ================================================================= */}
            {/* 6. MODAL DE DERROTA / NO CLASIFICACIÓN                            */}
            {/* ================================================================= */}
            {phase === 'eliminated' && (
                <div id="loserModal" className="overlay" style={{ display: 'flex' }}>
                    <TopGameLogo />
                    <div className="loser-box">
                        <div id="loserTitle" className="loser-title">
                            {eliminatedMessage}
                        </div>
                        {eliminatedTime && (
                            <div id="loserTimeDisplay" className="loser-time">
                                {eliminatedTime} s
                            </div>
                        )}
                        <div id="loserFooter" className="loser-footer">
                            ¡GRACIAS POR PARTICIPAR!
                        </div>
                    </div>
                    <InteraktiaFooter />
                </div>
            )}

            {/* ================================================================= */}
            {/* 7. MODALES LEGALES FLOTANTES (AVISO DE PRIVACIDAD Y TÉRMINOS)     */}
            {/* ================================================================= */}
            {showPrivacyModal && (
                <div className="legal-modal-backdrop" onClick={() => setShowPrivacyModal(false)}>
                    <div className="legal-modal-card" onClick={(e) => e.stopPropagation()}>
                        <div className="legal-modal-card__header">
                            <h3>Aviso de Privacidad</h3>
                            <button
                                type="button"
                                className="btn-close"
                                aria-label="Cerrar"
                                onClick={() => setShowPrivacyModal(false)}
                            >
                                &times;
                            </button>
                        </div>
                        <div className="legal-modal-card__body">
                            {DEFAULT_PRIVACY}
                        </div>
                        <div className="legal-modal-card__footer">
                            <button type="button" onClick={() => setShowPrivacyModal(false)}>
                                ¡ENTENDIDO!
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showTermsModal && (
                <div className="legal-modal-backdrop" onClick={() => setShowTermsModal(false)}>
                    <div className="legal-modal-card" onClick={(e) => e.stopPropagation()}>
                        <div className="legal-modal-card__header">
                            <h3>Términos y Condiciones</h3>
                            <button
                                type="button"
                                className="btn-close"
                                aria-label="Cerrar"
                                onClick={() => setShowTermsModal(false)}
                            >
                                &times;
                            </button>
                        </div>
                        <div className="legal-modal-card__body">
                            {DEFAULT_TERMS}
                        </div>
                        <div className="legal-modal-card__footer">
                            <button type="button" onClick={() => setShowTermsModal(false)}>
                                ¡ENTENDIDO!
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CronometerFan;
